'use client';

import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { useLocale } from '@/app/[lang]/providers/LocaleProvider';
import { useNavigationMenu, type NavigationNode } from '@/hooks/useNavigationMenu';
import { PATH_TRANSLATIONS } from '@/lib/pathTranslations';
import { canonicalPath, findNodeByPath } from '@/services/navigation.service';
import type { Locale } from '@/lib/localized-paths';

/**
 * Returns the navigation node that matches the current URL path.
 *
 * Navigation paths are built from the slugs of the active locale, so the URL is
 * matched as-is first. Some nodes are not localized by the backend (e.g. the
 * procurement menu keeps `procurement-information` in both locales) while the
 * frontend links to `/id/informasi-pengadaan/...`, so a canonical comparison is
 * used as a second attempt. The static PATH_TRANSLATIONS map is the last resort
 * for legacy URLs.
 */
export function useCurrentNavigationNode(): NavigationNode | undefined {
  const pathname = usePathname();
  const locale = useLocale();
  const { data: navigationMenu } = useNavigationMenu();

  return useMemo(() => {
    if (!navigationMenu?.length || !pathname) return undefined;

    // Strip locale prefix: /en/about-us/certifications-awards → /about-us/certifications-awards
    const withoutLocale = pathname.replace(new RegExp(`^/${locale}`), '') || '/';

    const direct = findNodeByPath(navigationMenu, withoutLocale);
    if (direct) return direct;

    const canonical = canonicalPath(locale as Locale, withoutLocale);
    const canonicalMatch = findNodeByPath(navigationMenu, canonical);
    if (canonicalMatch) return canonicalMatch;

    const translated = PATH_TRANSLATIONS[locale]?.[withoutLocale];
    return translated ? findNodeByPath(navigationMenu, translated) : undefined;
  }, [navigationMenu, pathname, locale]);
}
